import { useState } from 'react';

import {
  readLastUsedSignInMethod,
  type LastUsedSignInMethod,
} from '@/utils/last-used-sign-in-method';

/**
 * The sign-in method that last succeeded in this browser, or `undefined` when there is none
 * (or storage is unavailable — see `utils/last-used-sign-in-method`).
 *
 * Read once through a lazy `useState` initialiser rather than on every render: the value
 * cannot change while the page is open, since it is only ever written on the way out — in
 * the moment before the final redirect — so re-reading would only cost a synchronous
 * storage hit per render and risk the chip flickering.
 */
const useLastUsedSignInMethod = (): LastUsedSignInMethod | undefined => {
  // The setter is deliberately dropped: nothing in this page's lifetime may change the
  // snapshot, and `useState`'s lazy initialiser is the only React primitive that promises
  // to run exactly once per mount (`useMemo` is allowed to recompute and would re-read
  // storage — and could blink the chip away if a sign-out cleared it mid-page).
  // eslint-disable-next-line react/hook-use-state
  const [lastUsedSignInMethod] = useState(readLastUsedSignInMethod);

  return lastUsedSignInMethod;
};

export default useLastUsedSignInMethod;
